import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { AuthService } from '@app/core/auth/services/auth.service';
import { AuthTokenType } from '@app/core/auth/models/auth-token-type.enum';
import { TokenRefreshService } from '@app/core/auth/services/token-refresh.service';
import { Router } from '@angular/router';
import { AlertNewSnakbarService } from '@app/core/services/alert-new-snakbar.service';
import { Subject } from 'rxjs';

export interface HubInitConfig {
  url: string;
  automaticReconnect?: boolean;
  logLevel?: signalR.LogLevel;
}

@Injectable({ providedIn: 'root' })
export class GenericHubService {

  private hubConnection: signalR.HubConnection | null = null;
  private config: HubInitConfig | null = null;
  private isStarting = false;
  private isRefreshing = false;
  private isLoggingOut = false;

  // برای اینکه سرویس‌های دیگر اگر دوست داشتند بفهمند SignalR مشکل auth داشت
  unauthorized$ = new Subject<void>();
  errors$ = new Subject<any>();

  constructor(
    private authService: AuthService,
    private tokenRefreshService: TokenRefreshService,
    private router: Router,
    private alertSnakbar: AlertNewSnakbarService,
  ) { }

  init(config: HubInitConfig): void {
    this.config = {
      automaticReconnect: true,
      logLevel: signalR.LogLevel.Information,
      ...config
    };

    this.buildConnection();
  }

  private buildConnection(): void {
    if (!this.config) {
      throw new Error('GenericHubService: init(config) must be called before buildConnection().');
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.config.url, {
        accessTokenFactory: () =>
          this.authService.getRawAuthToken(AuthTokenType.AccessToken) || '',
        withCredentials: false
      })
      .configureLogging(this.config.logLevel!)
      .withAutomaticReconnect()
      .build();

    // هندل رویدادهای کلی
    this.hubConnection.onclose(error => {
      if (error) {
        this.handleConnectionError(error);
      }
    });

    this.hubConnection.onreconnecting(error => {
      if (error) {
        console.warn('SignalR reconnecting...', error);
      }
    });

    this.hubConnection.onreconnected(id => {
      console.log('SignalR reconnected.', id);
    });
  }

  connect(): void {
    if (!this.hubConnection) {
      this.buildConnection();
    }

    if (!this.hubConnection || !this.config) return;

    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isStarting) {
      return;
    }

    this.isStarting = true;

    this.hubConnection.start()
      .then(() => {
        console.log('SignalR connection started:', this.config!.url);
        this.isStarting = false;
      })
      .catch(err => {
        this.isStarting = false;
        this.handleConnectionError(err);
      });
  }

  disconnect(): void {
    if (!this.hubConnection) return;

    this.hubConnection.stop()
      .catch(err => console.error('Error stopping hub connection', err))
      .finally(() => {
        this.hubConnection = null;
      });
  }

  on<T>(eventName: string, callback: (data: T) => void): void {
    if (!this.hubConnection) {
      console.warn(`SignalR hubConnection is not initialized. event: ${eventName}`);
      return;
    }
    this.hubConnection.on(eventName, callback);
  }

  off(eventName: string): void {
    if (!this.hubConnection) return;
    this.hubConnection.off(eventName);
  }

  invoke<T = any>(methodName: string, ...args: any[]): Promise<T> {
    if (!this.hubConnection) {
      return Promise.reject('SignalR hubConnection is not initialized');
    }
    return this.hubConnection.invoke<T>(methodName, ...args)
      .catch(err => {
        this.handleConnectionError(err);
        return Promise.reject(err);
      });
  }

  // -------- Error / 401 / Refresh Token ----------

  private handleConnectionError(err: any): void {
    const status = this.getStatusCode(err);

    if (status === 401) {
      this.handle401();
      return;
    }

    if (status === 403) {
      this.alertSnakbar.snackBarError('AUTH.ERROR_403');
      this.errors$.next(err);
      return;
    }

    if (status === 500) {
      this.alertSnakbar.snackBarError('AUTH.ERROR_500');
      this.errors$.next(err);
      return;
    }

    // Network-like errors (connection dropped, etc.)
    if (!status) {
      // مثل همون status==0 / 408 تو اینترسپتور
      this.alertSnakbar.snackBarError('AUTH.CONNECTION_ERROR');
      this.errors$.next(err);
      return;
    }

    // default
    console.error('SignalR error:', err);
    this.errors$.next(err);
  }

  private handle401(): void {
    if (this.isLoggingOut) {
      return;
    }

    if (this.isRefreshing) {
      // اگر خواستی می‌تونی صبر کنی تا refresh تموم بشه و بعد دوباره connect کنی
      return;
    }

    this.isRefreshing = true;

    this.tokenRefreshService.handleRefreshToken()
      .subscribe({
        next: (result: any) => {
          this.isRefreshing = false;

          this.authService.setLoginSession(result);

          // بعد از refresh موفق → connection جدید با توکن جدید
          this.reconnectWithNewToken();
        },
        error: (err) => {
          this.isRefreshing = false;
          console.error('SignalR refresh token failed', err);
          this.forceLogout();
        }
      });
  }

  reconnectWithNewToken(): void {
    // اتصال قبلی را ببند
    if (this.hubConnection &&
      this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      this.hubConnection.stop().finally(() => {
        this.buildConnection();
        this.connect();
      });
    } else {
      this.buildConnection();
      this.connect();
    }
  }

  private forceLogout(): void {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    this.authService.logout(true);
    this.authService.deleteAuthTokens();
    this.router.navigate(['/auth']);

    this.unauthorized$.next();
  }

  private getStatusCode(err: any): number | null {
    // SignalR errorها HttpErrorResponse نیستن، باید یکم از تو message درش بیاریم
    if (err?.status && typeof err.status === 'number') {
      return err.status;
    }

    const msg = (err && err.message) ? err.message : err?.toString?.() ?? '';

    // نمونه پیام‌ها:
    // "Failed to complete negotiation with the server: Error: Status code '401'"
    const match = msg.match(/Status code '(\d+)'/i) || msg.match(/\b(\d{3})\b/);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }

    return null;
  }
}
